import React, { useMemo, useState, createContext, useEffect } from 'react';
import PropTypes from 'prop-types';

// Helper
import { getInitialContent } from '../../Requests/locationRequests';
import i18n from '../../i18n';
import { MAP_DEFAULTS } from '../LocationMap/BasicMapProps';

export const LocationContext = createContext();

const defaultUniquePlace = {
    display_name: 'Schuler Group GmbH & Schuler Pressen GmbH\n',
    locationLat: MAP_DEFAULTS.lat,
    locationLon: MAP_DEFAULTS.lng,
    place_id: '9999999',
};

const LocationContextProvider = ({ locale, children }) => {
    const [uniquePlace, setUniquePlace] = useState(defaultUniquePlace);
    const [loadingState, setLoadingState] = useState('initial');
    const [errorMessage, setErrorMessage] = useState({ message: '' });
    const [openedLocationId, setOpenedLocationId] = useState('');

    const [locationObjects, setLocationObjects] = useState([]);
    const [currentView, setCurrentView] = useState('map');
    const [resetMap, setResetMap] = useState(0);

    const timeoutPromise = () => new Promise((_, reject) => {

        setTimeout(() => {
            const error = new Error('error');
            error.message = i18n.t('error.timeout');
            reject(error); // Reject with error after timeout
        }, 15000); // 15 seconds
    });

    // INITIAL REQUEST
    useEffect(() => {
        setErrorMessage('');
        setLoadingState('loading');

        const fetchData = async () => {

            try {
                const response = await Promise.race([getInitialContent(locale), timeoutPromise()]);
                if (response?.success) {
                    setLocationObjects(response.data.entries);
                    setLoadingState('success');
                } else {
                    setErrorMessage(i18n.t('error.noSuccess'));
                    setLoadingState('error');
                }
            } catch (error) {
                if (error?.status === 404) {
                    setErrorMessage(i18n.t('error.notFound'));
                    setLoadingState('error');
                }
                if (error?.status === 503) {
                    setErrorMessage(i18n.t('error.timeout'));
                    setLoadingState('error');
                }
                setErrorMessage(String(error?.message || error));
                setLoadingState('error');
            }
        };

        fetchData().then();
    }, [locale]);

    const contextProps = useMemo(() => {
        return {
            uniquePlace,
            setUniquePlace,
            openedLocationId,
            setOpenedLocationId,
            locationObjects,
            setLocationObjects,
            loadingState,
            currentView,
            setCurrentView,
            errorMessage,
            setErrorMessage,
            resetMap,
            setResetMap,
        };
    }, [
        uniquePlace,
        setUniquePlace,
        openedLocationId,
        setOpenedLocationId,
        locationObjects,
        setLocationObjects,
        loadingState,
        currentView,
        setCurrentView,
        errorMessage,
        setErrorMessage,
        resetMap,
    ]);

    return (
        <LocationContext.Provider
            value={contextProps}
        >
            {children}
        </LocationContext.Provider>
    );
};


LocationContextProvider.propTypes = {
    locale: PropTypes.string,
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]),
};

export default LocationContextProvider;
